/**
 * Pack registry — runtime pack selection among build-time-known packs (ADR-066, INC-116).
 *
 * Introduced by INC-A of the `multi-pack-runtime` feature. This is the NEW mechanism that will
 * eventually replace the single build-time `active.ts` / `active-ui.ts` seam: it maps a pack's
 * **manifest id** to a registry entry carrying (a) static display metadata for the shared picker
 * (resolvable WITHOUT loading the pack chunk — SPEC D-5) and (b) two lazy loaders that
 * dynamic-`import()` the pack's data surface and UI surface as SEPARATE chunks (the two-seam
 * data/UI split — so node-side tooling never pulls `.vue` — survives inside each entry).
 *
 * INC-C (INC-118) wired the first live consumers: `resolved.ts` resolves a pack's surface by id
 * from this registry (the store shell + composables + `actorSheet` read it synchronously). The
 * live `active.ts` / `active-ui.ts` seams stay PRESENT (still used by not-yet-migrated `.vue`
 * components and seeded as the tooling default in `resolved.ts`); seam retirement is INC-E.
 *
 * INVARIANTS
 *  - `pack_id` / manifest id is an OPEN value (SPEC §10.1, VISION §3): NOT a DB enum and NOT a
 *    closed ContentPack-schema type. The ONLY closed, compile-time-checked surface is HOST-SIDE
 *    — the `PackId` union below, which enumerates exactly the *shippable* packs. The engine and
 *    the DB schema stay agnostic to which pack ids exist.
 *  - synth-2d6 is a conformance fixture (M1 / GAPS Q3) and is NEVER added to the registry under
 *    any `VITE_PACKS` filter (guard test asserts its absence).
 */
import type { Component, ComputedRef } from 'vue'
import type { CharacterExport, CharRole, ResourceUsage } from '@/types/db'
import type { DiceRoll, DiceSpec } from '@/types/dice'
import dndSrd521Notice from './dnd-srd-5.2.1/NOTICE?raw'
import type { ContentPack, OptionProvider } from '@/engine/metamodel/types'
import type {
  ActiveEffect,
  ApplyStatusContext,
  BestiaryEntry,
  DamageType,
  Defenses,
  EffectExpiry,
  PowerModifier,
  QuickNpcField,
  ResolvedTarget,
  Status,
  StatusApplies,
} from './contract/types'
import type { DamageApplication, DamageBlow, DamageTargetResult } from './contract/damage'
import type { InitiativeAffordance, InitiativeContext } from './contract/initiative'

/**
 * Pack-neutral host contract types re-exported from the registry (INC-118 / INC-C). Consumers
 * that only need the TYPE of a seam symbol import it from here instead of the `active-ui` seam,
 * so the runtime-value move to the resolved surface (`resolved.ts`) does not drag the type import
 * back onto the seam. These are the `content-packs/contract` neutral definitions — identical for
 * every pack (ADR-042); genuinely pack-specific types stay with their reference pack.
 */
export type {
  ActiveEffect,
  ApplyStatusContext,
  DamageType,
  Defenses,
  EffectExpiry,
  PowerModifier,
  QuickNpcField,
  ResolvedTarget,
  Status,
  StatusApplies,
} from './contract/types'
export type { InitiativeContext } from './contract/initiative'

/**
 * A host choice-picker binding. Structurally identical across every pack's `host/choicePicker`
 * (dnd-4.5 / Cairn declare the same shape), so it is promoted to a neutral host-contract type.
 */
export interface ChoiceBinding {
  selected: ComputedRef<string[]>
  choose: (id: string) => void
}

/**
 * Host-side closed union of the manifest ids of shippable packs (SPEC §10.1). Compile-time
 * only: it gives the host type-safety over pack identity WITHOUT turning pack identity into a
 * data-model enum. synth-2d6 is deliberately absent.
 */
export type PackId = 'dnd-4.5-dab' | 'cairn' | 'cairn-2e' | 'dnd-srd-5.2.1'

/**
 * Data surface a pack's engine-facing seam (`active.<pack>.ts`) exposes. Pack-neutral and
 * node/tooling-safe (no `.vue`). Promoted from the existing `active.<pack>` export shape.
 */
export interface PackDataSurface {
  activePack: ContentPack
}

/**
 * UI surface a pack's host-facing seam (`active-ui.<pack>.ts`) exposes.
 *
 * INC-A curated a NEUTRAL SUBSET; INC-C (INC-118) widens it to the host-consumed **value**
 * contract the registry-driven consumers pull at runtime (the store setup, the choice-picker
 * factories, the effects projector), which is what `loadUi()` returns and both packs must satisfy.
 *
 * The two packs' seams genuinely diverge on the pack-specific PARAMETER types of these value
 * members (dnd-4.5's `Store` vs Cairn's `CairnChoiceStore`; dnd's `ActorEffect[]` carrier vs
 * Cairn's `ActiveEffect[]`). Resolution (ADR-066, option (a)): keep ONE shared shape and type the
 * genuinely-pack-specific parameters as `never` — every pack's concrete signature is assignable
 * here (contravariance: any parameter type ⊇ `never`), and the host consumer casts the resolved
 * member to its REFERENCE-pack (dnd-4.5) type — sourced via an `import type` — before calling it
 * with a same-pack-typed argument. Return/neutral positions keep their real contract types.
 *
 * Members that are host-consumed only via `.vue` component seams NOT yet moved to the registry
 * (`buildQuickNpcData`, `reduceByResistance`, sheet prop types, …) are added by INC-D alongside
 * the components that resolve them, so every member of this contract is exercised, not speculative.
 * dnd-only exports never host-consumed through the seam (`getModifier`, `createDndCharacterLayer`)
 * are deliberately absent.
 */
export interface PackUiSurface {
  stepComponents: Record<string, Component>
  CharacterSummary: Component
  WizardAside: Component
  CharacterSheet: Component
  MobileCharacterSheet: Component
  CharacterCard: Component
  STATUSES: readonly Status[]
  initiativeAffordance: (ctx: InitiativeContext) => InitiativeAffordance
  // dnd-4.5 keeps its inline HP path and declares NO pack damage hook (undefined); Cairn ships a
  // concrete one (INC-110 / ADR-061). 5E ships neither and resolves each target through
  // `damageTarget` instead (ADR-112).
  damageApplication: DamageApplication | undefined
  /**
   * ADR-112 D1: resolve one target of a damage roll when the pack has no `damageApplication`.
   * The pack owns the whole blow (Resistance/Immunity/Vulnerability, 0 HP, the conditions) and
   * returns the usage it leaves plus the saves it owes as pack roll ids (ADR-112 D2).
   */
  damageTarget?: (
    target: {
      id: string
      data: CharacterExport | undefined
      usage: ResourceUsage | undefined
      role: CharRole | undefined
    },
    blow: DamageBlow,
  ) => DamageTargetResult
  // ── INC-C additions — host-consumed runtime value members (store + choice + effects) ──
  // Pinia setup: zero-arg factory returning the pack's store shape. The pack-agnostic store shell
  // casts this to the reference-pack setup type so the host keeps its contract-subset typing.
  characterStoreSetup: () => unknown
  // Choice-picker factories: the `store` parameter is the pack's OWN store shape (typed `never`
  // here, see class comment). Returns are the neutral contract shapes.
  makeOptionProvider: (store: never) => OptionProvider
  makeChoiceBindings: (store: never) => (choiceId: string) => ChoiceBinding
  // Effects projector: the persisted-carrier parameter is pack-specific (`never` here); the
  // projection is the neutral `ActiveEffect` view.
  packEffects: (effects: never) => ActiveEffect[]
  // ── INC-D additions — DM-combat members the flow `.vue` components resolve BY campaign pack ──
  // Quick-NPC stat-block form fields the pack wants rendered (neutral key union, INC-114).
  quickNpcFields: readonly QuickNpcField[]
  // Builds a campaign-actor export envelope from the pack's OWN quick-NPC input. The input is
  // pack-specific (typed `never` per the reference-cast pattern above); the consumer casts it to
  // its reference-pack (dnd-4.5) input type. The envelope itself is the neutral `CharacterExport`.
  buildQuickNpcData: (input: never) => CharacterExport
  // ── Roll-log display vocabulary (INC-132) — resolved by the CAMPAIGN's pack so the generic
  // `DiceLog` prints the labels of whichever system rolled. Both take the OPEN transport strings
  // (ADR-031: `RollDamage.damageType`, `AttackResolution.defense`); a key the pack does not name
  // renders verbatim, and dnd-4.5's damage label collapses its `physical` default to ''.
  damageTypeLabel: (type: string | undefined) => string
  defenseLabel: (key: string) => string
  // Neutral damage-resistance reducer: subtracts the target's per-type resistance from `amount`.
  reduceByResistance: (
    amount: number,
    damageType: DamageType | undefined,
    resistances: Partial<Record<DamageType, number>> | undefined,
  ) => number
  // ── Status / initiative projections — consumed by CampaignView's combat tracker; resolved by the
  // CAMPAIGN's pack so a Cairn campaign runs Cairn's rules. The `effects` carrier is the pack's OWN
  // ActiveEffect shape (typed `never` per the reference-cast pattern above, like `packEffects`); the
  // consumer casts the arg. `ids`/`ctx`/returns are neutral contract types. ──
  activeStatusIdsOf: (effects: never, characterId?: string) => string[]
  resolveStatuses: (ids?: string[]) => Status[]
  /**
   * Declared with METHOD syntax on purpose: its `ctx` is the shared `ApplyStatusContext`, whose
   * `EffectExpiry` (ADR-112/ADR-113) is wider than the reference pack's own vocabulary, and a pack
   * implementing its own narrower `ApplyStatusContext` must still be assignable here — method
   * parameters are compared bivariantly, property-typed function ones are not (ADR-066's
   * reference-cast pattern: the caller passes a same-pack-typed argument).
   */
  withAppliedStatuses(
    effects: never,
    statusIds: string[],
    ctx: ApplyStatusContext,
  ): ActiveEffect[] | null
  initiativeTieBreak: (ctx: InitiativeContext) => number
  NPC_INITIATIVE_KEY: number | null
  /**
   * The sort key an actor has in `round`, from its synced key. Absent ⇒ the key as stored (dnd, Cairn 1e).
   * `key` is `usage.initiative`, or the pack's NPC key for an unrolled NPC; `undefined` = not rolled.
   * Returns a number (the key this round), `undefined` (not rolled, as today) or `null` (no turn this round).
   * Cairn 2e side initiative (ADR-094, S3p §Initiative).
   */
  initiativeKeyForRound?: (
    key: number | undefined,
    role: 'pc' | 'npc',
    round: number,
  ) => number | null | undefined
  /** ADR-121 D1: identical NPCs share a key; one roll serves an unrolled group. Absent ⇒ no groups. */
  initiativeGroupKey?: (data: CharacterExport | undefined) => string | undefined
  /** ADR-121 D2: whether `holder` may swap its initiative with `ally`. Absent ⇒ no swap. */
  canSwapInitiative?: (
    holder: { data: CharacterExport | undefined; usage: ResourceUsage | undefined },
    ally: { data: CharacterExport | undefined; usage: ResourceUsage | undefined },
  ) => boolean
  // ── Roll requests (X2, ADR-098; SPEC §3.2) ──
  // The rolls this pack lets the GM ask for, in its own words (Cairn: the three saves). Listed in
  // the GM's menus; absent ⇒ only "Other roll…" (dnd-4.5 declares none).
  requestRolls?: readonly { id: string; label: string }[]
  // Resolve ANY pack roll id — a request's, a sheet's `rollId`, a `gmRolls` id with no character — on
  // the roll's client, from the character's CURRENT state, into the spec to roll and (optionally) the
  // rule line and the outcome word for a total. The inputs mirror `DamageApplication.contextFromActor`.
  // Absent, or an unknown id ⇒ no spec (refused).
  resolveRequestRoll?: (
    id: string,
    data: CharacterExport | undefined,
    usage: ResourceUsage | undefined,
  ) => {
    spec: DiceSpec
    rule?: string
    outcome?: (total: number) => string
    /**
     * ADR-104 D7: the usage this result leaves — the write its outcome makes, committed with the
     * roll (a pass on `recover:panicked` drops the Panicked entries). Absent ⇒ the roll writes
     * nothing. Additive. `natural` (ADR-111 D4): the stored roll's kept first-group faces
     * (`naturalOf`), for a rule that reads the die (5E's death save).
     */
    apply?: (
      total: number,
      usage: ResourceUsage | undefined,
      natural?: number,
    ) => ResourceUsage
    /**
     * ADR-112 D2: the damage this roll's result deals to the roller's own character (a save for
     * half). `amount` 0 ⇒ nothing to commit. The host turns it into a damage roll (`damageFor`).
     */
    damage?: (
      total: number,
      natural?: number,
    ) => { kind: string; damageType?: string; amount: number }
  } | undefined
  // ── 4b-2 (ADR-103): the GM's own pack rolls, rolled for no character (Cairn: Reaction, the Die of
  // Fate); each id is resolved by `resolveRequestRoll(id, undefined, undefined)`. Absent ⇒ none (dnd-4.5).
  gmRolls?: readonly { id: string; label: string }[]
  /**
   * ADR-113 D3: the abilities a save-ends effect may name; present ⇒ the condition editor asks for
   * ability + DC and writes them onto the effect's expiry. Absent ⇒ save-ends applies on click, as
   * before (dnd-4.5, Cairn).
   */
  saveAbilities?: readonly { id: string; label: string }[]
  /**
   * 4b-3 (ADR-104): the max HP the host's meters show, from the sheet and session state. Absent ⇒
   * `actorField(data, 'hp')` (dnd-4.5), so a pack without the rule is unchanged.
   */
  maxHpOf?: (data: CharacterExport | undefined, usage: ResourceUsage | undefined) => number
  /**
   * ADR-110 §2: the pack's own attack scalars for one aimed target, merged into the scope the host
   * decides the outcome with (`natural`, `total`, `target`). 5E supplies `critMin` and `autoCrit`;
   * absent ⇒ the ladder gets the three host keys alone (dnd-4.5 and Cairn declare nothing).
   */
  attackScope?: (ctx: {
    attacker: CharacterExport | undefined
    target: CharacterExport | undefined
    targetId: string | undefined
    targetUsage: ResourceUsage | undefined
    kind: string
  }) => Record<string, number>
  /**
   * ADR-113 D4: the d20 an attack rolls, once its ONE target is known (none when it has 0 or
   * several). The pack folds its own conditions (the attacker's, the target's) into the mode and
   * relabels the roll. Absent ⇒ the declared spec, unchanged (dnd-4.5, Cairn).
   * ADR-120 D3: `spend` is the ids of entries on the attacker's own usage the roll used; the host
   * drops them after the roll.
   */
  attackSpec?: (input: {
    spec: DiceSpec
    label: string
    kind: string
    attacker: { id: string; data: CharacterExport | undefined; usage: ResourceUsage | undefined }
    target?: { id: string; data: CharacterExport | undefined; usage: ResourceUsage | undefined }
  }) => { spec: DiceSpec; label: string; spend?: string[] }
  /**
   * ADR-110 §3: the spec a Critical Hit's damage is rolled with, from the damage spec as declared.
   * Absent ⇒ no doubling (dnd-4.5, Cairn).
   */
  critDamage?: (spec: DiceSpec) => DiceSpec
  /**
   * ADR-110 §4: what this pack lets `roll` reroll, and what that spends. The host offers each one on
   * the roller's own latest roll of that character. Absent ⇒ no reroll (dnd-4.5, Cairn).
   */
  rerollOffers?: (
    roll: DiceRoll,
    data: CharacterExport | undefined,
    usage: ResourceUsage | undefined,
  ) => { id: string; label: string; index: number; spend?: Partial<ResourceUsage> }[]
  /**
   * ADR-111 D2: the reason this actor gets no restores from any recovery (5E: "0 HP" — a rest needs
   * at least 1 Hit Point). Treated like `skipIfStatus`: excluded, and a PC is named in the skip line.
   * Absent ⇒ no pack skip (dnd-4.5, Cairn).
   */
  recoverySkip?: (
    data: CharacterExport | undefined,
    usage: ResourceUsage | undefined,
    role: 'pc' | 'npc',
  ) => string | undefined
  /**
   * ADR-111 D2: the usage a recovering actor is left with after the key drop and the effect prune
   * (5E: a Short Rest's per-feature recharge; a Long Rest's Exhaustion −1 and Human Heroic
   * Inspiration). The host writes the changed keys. Absent ⇒ nothing more (dnd-4.5, Cairn).
   */
  afterRecovery?: (
    recoveryId: string,
    data: CharacterExport | undefined,
    usage: ResourceUsage | undefined,
  ) => ResourceUsage
  /**
   * ADR-111 D5: the pack rolls this character owes while it is its turn (5E: the death save while
   * dying), as the Now card's buttons. Each id resolves through `resolveRequestRoll`. Absent ⇒ none.
   */
  turnStartRolls?: (
    data: CharacterExport | undefined,
    usage: ResourceUsage | undefined,
    role: 'pc' | 'npc',
  ) => { id: string; label: string }[]
  /** ADR-117 D1: the game's monsters, loaded when a GM opens the Bestiary panel. Absent ⇒ none. */
  bestiary?: () => Promise<BestiaryEntry[]>
  /** ADR-117 D4: the defenses an attack meets now (5E: a Wild Shape form's AC), merged over the
   *  sheet's `defenses`. Absent ⇒ the sheet's own (dnd-4.5, Cairn). */
  targetDefenses?: (
    data: CharacterExport | undefined,
    usage: ResourceUsage | undefined,
  ) => Partial<Defenses>
  /** ADR-118 D1: the NPC's window is the pack's sheet when this says so. Absent ⇒ its card (Cairn, dnd-4.5). */
  npcOpensSheet?: (data: CharacterExport | undefined) => boolean
}

/**
 * One shippable pack. Display metadata (`name`, `blurb`, `art`) is static and resolvable without
 * invoking a loader (SPEC D-5). `loadData` / `loadUi` dynamic-`import()` the two seams as
 * separate chunks.
 */
export interface PackRegistryEntry {
  id: PackId
  /** INC-231: the short label: game + edition ("Cairn 2e") or game + rules year ("5e (2024)"); never "SRD" or a version number. */
  name: string
  /** INC-231: one line under the label (picker, /help); the official full name or the SRD version goes here. */
  blurb: string
  art: string
  /** ADR-099: usable only with the account's own grant (restricted_packs, migration 031). */
  restricted?: true
  /** ADR-109: the licence notice, verbatim, for /help#credits, the picker's License link and every
   *  downloaded export file. Plain text; its URLs render as links. */
  notice?: string
  /** ADR-109: the `/builder?pack=` value when the manifest id must not reach the address bar. */
  slug?: string
  loadData: () => Promise<PackDataSurface>
  loadUi: () => Promise<PackUiSurface>
}

/**
 * Placeholder picker art (SPEC §10.4). A real per-pack image is deferred content work; a shared
 * inline data-URI keeps the registry self-contained and pulls no asset dependency at INC-A.
 */
const PLACEHOLDER_ART =
  'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2016%2016%22%3E%3Crect%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22%23888%22%2F%3E%3C%2Fsvg%3E'

/**
 * All shippable pack entries, keyed for filtering by manifest id. synth-2d6 is NOT here (and
 * never will be). Order is display order for the picker.
 */
const ALL_ENTRIES: PackRegistryEntry[] = [
  {
    id: 'dnd-4.5-dab',
    name: 'D&D 4.5 Revised',
    blurb: 'The house-ruled 4e-derived system — five abilities, healing surges, powers.',
    art: PLACEHOLDER_ART,
    restricted: true, // ADR-099: usable only with the account's own grant
    loadData: () => import('./active.dnd-4.5'),
    loadUi: () => import('./active-ui.dnd-4.5'),
  },
  {
    id: 'cairn',
    name: 'Cairn 1e',
    blurb: 'Cairn - First Edition, a classless survival RPG.',
    art: PLACEHOLDER_ART,
    loadData: () => import('./active.cairn'),
    loadUi: () => import('./active-ui.cairn'),
  },
  {
    id: 'cairn-2e',
    name: 'Cairn 2e',
    blurb: 'Cairn Second Edition, with Bonds and Omens.',
    art: PLACEHOLDER_ART,
    loadData: () => import('./active.cairn-2e'),
    loadUi: () => import('./active-ui.cairn-2e'),
  },
  {
    id: 'dnd-srd-5.2.1',
    name: '5e (2024)',
    blurb: '5E compatible, limited to the SRD 5.2.1.',
    art: PLACEHOLDER_ART,
    notice: dndSrd521Notice.trim(),
    slug: '5e-2024',
    loadData: () => import('./active.dnd-srd-5.2.1'),
    loadUi: () => import('./active-ui.dnd-srd-5.2.1'),
  },
]

/**
 * Build-time white-label filter. `VITE_PACKS` maps a human token to a manifest id in this ONE
 * place; unset = all shippable packs, a single token = that pack only. A single-entry registry
 * lets the picker auto-skip (INC-D). synth-2d6 is unreachable regardless of the filter.
 */
export const TOKEN_TO_ID: Record<NonNullable<ImportMetaEnv['VITE_PACKS']>, PackId> = {
  'dnd-4.5': 'dnd-4.5-dab',
  cairn: 'cairn',
  'cairn-2e': 'cairn-2e',
  'dnd-srd-5.2.1': 'dnd-srd-5.2.1',
}

/** ADR-099 / Q131: the ids marked `restricted` across every shippable entry (the `VITE_PACKS` filter
 *  does not apply). Must equal the migrations' `restricted_packs` set (restrictedPacks.spec.ts). */
export const RESTRICTED_PACK_IDS: readonly string[] = ALL_ENTRIES.filter((e) => e.restricted).map(
  (e) => e.id,
)

const filterToken = import.meta.env.VITE_PACKS
const selectedEntries = filterToken
  ? ALL_ENTRIES.filter((entry) => entry.id === TOKEN_TO_ID[filterToken])
  : ALL_ENTRIES

/** The registry: manifest id → entry, honoring the `VITE_PACKS` filter. */
export const packRegistry: Map<string, PackRegistryEntry> = new Map(
  selectedEntries.map((entry) => [entry.id, entry]),
)

/**
 * The display name of a pack id (B6: the list shows the game in words). Reads every shippable
 * entry, not only this build's `VITE_PACKS` filter, so a campaign or character of a pack this build
 * leaves out still names its game. An unknown id renders verbatim.
 */
export function packName(id: string): string {
  return ALL_ENTRIES.find((entry) => entry.id === id)?.name ?? id
}

/** ADR-109: the licence notice a pack's downloaded export carries, if it has one. */
export function packNotice(id: unknown): string | undefined {
  return ALL_ENTRIES.find((entry) => entry.id === id)?.notice
}

/** ADR-109: the `/builder?pack=` value of a pack id: its slug, else the id. */
export function packSlug(id: PackId): string {
  return ALL_ENTRIES.find((entry) => entry.id === id)?.slug ?? id
}

/** ADR-109: a `/builder?pack=` value back to a manifest id. A manifest id still resolves, so old
 *  links and the QA scripts keep working. Unknown values pass through for `resolveDraftPack`. */
export function packIdFromSlug(value: unknown): unknown {
  return ALL_ENTRIES.find((entry) => entry.slug === value)?.id ?? value
}
